import dayjs from 'dayjs';

import { applyStandingRates, mapRunDetail } from './usePayrollRun.adapter';
import { PAYROLL_LINE_CODES, PayrollRun } from './usePayrollRun.interface';

import {
    PayScheduleSummary,
    PayrollBlocker,
    PayrollLine,
    PayrollRunDetail,
    PayrollRunItem,
    PayrollTotals,
    TeamMember,
} from 'services/payroll/api/generated/v1/model';

/**
 * Fixtures for the review-and-run flow, in the **wire shape** (`PayrollRunDetail`). These are what
 * the MSW handlers serve, so the demo drives the real generated hook, the real URL and the real
 * adapter rather than a second code path. The view-model exports at the bottom are those same
 * fixtures run through the adapter — one source, two shapes, and the adapter is the only thing
 * between them.
 *
 * Dates are derived from today, never hardcoded: a fixed date drifts into the past and the
 * deadline silently renders "Overdue", which is a different state from the one under design
 * review. `usePayrollOverview.mocks.ts` documents the same rule.
 *
 * The people are picked to exercise the column projections' placeholder logic — one salaried, one
 * hourly with overtime, and one with genuine `'0.00'` amounts alongside values that are truly
 * absent (no line at all). `'0.00'` must render `$0.00`; only an absent line may render a
 * placeholder.
 *
 * **The numbers have to add up.** These fixtures are what design review and Storybook look at, and
 * a Company-pays column whose footer disagrees with the rows above it reads as a render bug rather
 * than as a fixture typo. `payrollTableColumns.test.ts` asserts the invariants so they cannot
 * drift: each person's employer subtotal is gross plus their employer-paid lines, `company_debit`
 * is the sum of those, and each `net_pay` is gross less the employee's own lines plus
 * reimbursements.
 */

const TODAY = dayjs();
const ISO_DATE = 'YYYY-MM-DD';

/** The run under review covers the current month and pays out shortly after it closes. */
const PERIOD_START = TODAY.startOf('month');
const PERIOD_END = TODAY.endOf('month');
/** 4:00pm on the deadline day — a moment, not a date, because that is what the wire sends. */
const DEADLINE = TODAY.add(18, 'day').hour(16).startOf('hour');

const MONTHLY_SCHEDULE: PayScheduleSummary = {
    pay_schedule_uuid: '5c9b1e77-3a42-4d81-8f0c-2b6e7d4a9c10',
    frequency: 'monthly',
    custom_name: '',
    anchor_pay_date: null,
    auto_payroll_enabled: false,
    day_1: null,
    day_2: null,
    is_primary: true,
    period_end_anchor: null,
};

type LineKind = PayrollLine['kind'];
type LineParty = PayrollLine['party'];

const line = (
    kind: LineKind,
    party: LineParty,
    code: string,
    label: string,
    amount: string | null,
    hours: string | null = null
): PayrollLine => ({ kind, party, code, label, amount, hours, job_ref: '' });

const earning = (code: string, label: string, amount: string, hours: string | null = null) =>
    line('earning', 'employer', code, label, amount, hours);
const timeOff = (code: string, label: string, hours: string) => line('time_off', 'employer', code, label, null, hours);
const employerTax = (label: string, amount: string) => line('tax', 'employer', label.toLowerCase(), label, amount);
const employeeTax = (label: string, amount: string) => line('tax', 'employee', label.toLowerCase(), label, amount);
const employerBenefit = (label: string, amount: string) =>
    line('benefit', 'employer', label.toLowerCase(), label, amount);
const employeeBenefit = (label: string, amount: string) =>
    line('benefit', 'employee', label.toLowerCase(), label, amount);
const deduction = (label: string, amount: string) => line('deduction', 'employee', label.toLowerCase(), label, amount);
const reimbursement = (label: string, amount: string) =>
    line('reimbursement', 'employer', 'reimbursement', label, amount);

const worker = (
    overrides: Partial<PayrollRunItem> & Pick<PayrollRunItem, 'item_uuid' | 'worker_uuid' | 'worker_name'>
) =>
    ({
        job_title: '',
        is_owner: false,
        gross_pay: null,
        net_pay: null,
        payment_method: 'Direct Deposit',
        excluded: false,
        lines: [],
        ...overrides,
    } as PayrollRunItem);

/**
 * Salaried at $76,000/yr on a monthly schedule, so the roster join below lands exactly on this
 * run's gross and the row is *not* flagged. Base pay arrives as `other` — the provider's own
 * vocabulary has no code Collective owns for a salary line — and the $500 bonus is what the
 * Additional column shows.
 */
const salariedWorker = worker({
    item_uuid: 'a1b2c3d4-0001-4c3a-9b7e-1f2a3b4c5d61',
    worker_uuid: 'e1f0a2b3-0001-4c3a-9b7e-1f2a3b4c5d61',
    worker_name: 'Jon Kuminga',
    job_title: 'Engineer',
    gross_pay: '6333.33',
    net_pay: '4788.26',
    lines: [
        earning('other', 'Salary', '5833.33'),
        earning(PAYROLL_LINE_CODES.bonus, 'Bonus', '500.00'),
        // A genuine zero: this person took no commission this period, which is a fact worth showing.
        earning(PAYROLL_LINE_CODES.commission, 'Commission', '0.00'),
        timeOff(PAYROLL_LINE_CODES.pto, 'Paid Time Off Policy', '8.0'),
        timeOff(PAYROLL_LINE_CODES.sick, 'Sick Policy', '0.0'),
        employeeTax('Federal Income Tax', '812.15'),
        employeeTax('Social Security', '392.67'),
        employeeTax('Medicare', '65.65'),
        employerTax('Social Security (Employer)', '392.67'),
        employerTax('Medicare (Employer)', '91.83'),
        employerTax('FUTA', '42.00'),
        employerTax('CA SUI', '78.82'),
        employerBenefit('401(k)', '420.00'),
        employeeBenefit('401(k)', '250.00'),
        deduction('Health Insurance', '150.00'),
        reimbursement('Home office', '125.40'),
    ],
});

/**
 * Hourly on purpose: this is the fixture that proves the name cell reads the unit rather than
 * assuming a year, and the one whose standing pay stays unknown because rate × hours is the very
 * figure under review.
 */
const hourlyWorker = worker({
    item_uuid: 'a1b2c3d4-0002-4c3a-9b7e-1f2a3b4c5d62',
    worker_uuid: 'e1f0a2b3-0002-4c3a-9b7e-1f2a3b4c5d62',
    worker_name: 'Brandin Podziemski',
    job_title: 'Technician',
    gross_pay: '3480.00',
    net_pay: '2781.20',
    lines: [
        earning(PAYROLL_LINE_CODES.regularHours, 'Regular Hours', '3200.00', '160.0'),
        earning(PAYROLL_LINE_CODES.overtime, 'Overtime', '200.00', '6.5'),
        earning(PAYROLL_LINE_CODES.doubleOvertime, 'Double Overtime', '0.00', '0.0'),
        earning(PAYROLL_LINE_CODES.cashTips, 'Cash Tips', '80.00'),
        timeOff(PAYROLL_LINE_CODES.sick, 'Sick Policy', '4.0'),
        employeeTax('Federal Income Tax', '432.60'),
        employeeTax('Social Security', '215.76'),
        employeeTax('Medicare', '50.44'),
        employerTax('Social Security (Employer)', '215.76'),
        employerTax('Medicare (Employer)', '50.44'),
        employerTax('FUTA', '20.88'),
        employerTax('CA SUI', '45.26'),
        // A genuine zero — nothing was reimbursed, as opposed to a reimbursement we failed to load.
        reimbursement('Mileage', '0.00'),
    ],
});

/**
 * The load-failure case, inline: this person's reimbursements and time off came back absent — no
 * line of either kind. Nothing else about the run is broken, so the row still renders, with
 * placeholders in exactly those two cells and real numbers everywhere else. No compensation on
 * file either, so the name cell falls back to one line.
 */
const partiallyLoadedWorker = worker({
    item_uuid: 'a1b2c3d4-0003-4c3a-9b7e-1f2a3b4c5d63',
    worker_uuid: 'e1f0a2b3-0003-4c3a-9b7e-1f2a3b4c5d63',
    worker_name: 'Moses Moody',
    job_title: 'Analyst',
    payment_method: 'Check',
    gross_pay: '4166.67',
    net_pay: '3125.00',
    lines: [
        earning('other', 'Salary', '4166.67'),
        employeeTax('Federal Income Tax', '683.34'),
        employeeTax('Social Security', '258.33'),
        employeeTax('Medicare', '100.00'),
        employerTax('Social Security (Employer)', '258.33'),
        employerTax('Medicare (Employer)', '60.42'),
        employerTax('FUTA', '25.00'),
        employerTax('CA SUI', '54.25'),
        deduction('Garnishment', '0.00'),
    ],
});

/**
 * Excluded from this run — the provider skips them from calculation, so they don't count toward
 * the totals, but the rows still carry real figures (what they'd normally be paid) rather than
 * nulls, matching the design's excluded-employee disclosure (Figma `5509:6829`).
 */
const excludedSalariedWorker = worker({
    item_uuid: 'a1b2c3d4-0004-4c3a-9b7e-1f2a3b4c5d64',
    worker_uuid: 'e1f0a2b3-0004-4c3a-9b7e-1f2a3b4c5d64',
    worker_name: 'Priya Reddy',
    job_title: 'Designer',
    excluded: true,
    gross_pay: '5200.00',
    net_pay: '3776.00',
    lines: [
        earning('other', 'Salary', '5200.00'),
        timeOff(PAYROLL_LINE_CODES.pto, 'Paid Time Off Policy', '0.0'),
        employeeTax('Federal Income Tax', '706.20'),
        employeeTax('Social Security', '322.40'),
        employeeTax('Medicare', '75.40'),
        employerTax('Social Security (Employer)', '322.40'),
        employerTax('Medicare (Employer)', '75.40'),
        employerTax('FUTA', '35.00'),
        employerTax('CA SUI', '64.80'),
        employerBenefit('401(k)', '300.00'),
        employeeBenefit('401(k)', '200.00'),
        deduction('Health Insurance', '120.00'),
        reimbursement('Mileage', '0.00'),
    ],
});

const excludedHourlyWorker = worker({
    item_uuid: 'a1b2c3d4-0005-4c3a-9b7e-1f2a3b4c5d65',
    worker_uuid: 'e1f0a2b3-0005-4c3a-9b7e-1f2a3b4c5d65',
    worker_name: 'Tobias Lindgren',
    job_title: 'Technician',
    excluded: true,
    gross_pay: '2880.00',
    net_pay: '2304.94',
    lines: [
        earning(PAYROLL_LINE_CODES.regularHours, 'Regular Hours', '2880.00', '160.0'),
        timeOff(PAYROLL_LINE_CODES.sick, 'Sick Policy', '0.0'),
        employeeTax('Federal Income Tax', '354.74'),
        employeeTax('Social Security', '178.56'),
        employeeTax('Medicare', '41.76'),
        employerTax('Social Security (Employer)', '178.56'),
        employerTax('Medicare (Employer)', '41.76'),
        employerTax('FUTA', '17.28'),
        employerTax('CA SUI', '37.15'),
        reimbursement('Mileage', '0.00'),
    ],
});

const MOCK_TOTALS: PayrollTotals = {
    gross_pay: '13980.00',
    net_pay: '10694.46',
    employee_taxes: '3010.94',
    employer_taxes: '1335.66',
    /** The employees' own 401(k) contribution — the salaried row's. Not their deductions. */
    employee_benefits: '250.00',
    employer_benefits: '420.00',
    reimbursements: '125.40',
    company_debit: '15861.06',
};

const ZERO_TOTALS: PayrollTotals = {
    gross_pay: '0.00',
    net_pay: '0.00',
    employee_taxes: '0.00',
    employer_taxes: '0.00',
    employee_benefits: '0.00',
    employer_benefits: '0.00',
    reimbursements: '0.00',
    company_debit: '0.00',
};

/** The happy path on the wire: three people, one of each shape above. */
export const MOCK_PAYROLL_RUN_DETAIL: PayrollRunDetail = {
    payroll_uuid: '8f2a1c14-6d3b-4f8e-9a21-5b7c0e4d1f33',
    external_payroll_id: '',
    amends_payroll_uuid: null,
    replaced_by: null,
    as_of: TODAY.subtract(20, 'minute').toISOString(),
    pay_schedule: MONTHLY_SCHEDULE,
    pay_period_start: PERIOD_START.format(ISO_DATE),
    pay_period_end: PERIOD_END.format(ISO_DATE),
    check_date: TODAY.add(23, 'day').format(ISO_DATE),
    debit_date: TODAY.add(18, 'day').format(ISO_DATE),
    payroll_deadline: DEADLINE.toISOString(),
    reversal_deadline: null,
    off_cycle: false,
    processed: false,
    posted_at: null,
    calculated_at: TODAY.subtract(20, 'minute').toISOString(),
    display_status: 'NEXT',
    status: 'draft',
    payroll_type: 'regular',
    is_first_payroll: false,
    totals: MOCK_TOTALS,
    items: [salariedWorker, hourlyWorker, partiallyLoadedWorker],
    blockers: [],
    can_prepare: true,
    can_submit: true,
    can_cancel: false,
    can_skip: true,
};

/**
 * `MOCK_PAYROLL_RUN_DETAIL` plus two people excluded from the run. `totals` is untouched — it
 * already reflects only the three included people, which is exactly what the wire does.
 */
const EXCLUDED_EMPLOYEES_PAYROLL_RUN_DETAIL: PayrollRunDetail = {
    ...MOCK_PAYROLL_RUN_DETAIL,
    items: [...MOCK_PAYROLL_RUN_DETAIL.items, excludedSalariedWorker, excludedHourlyWorker],
};

/**
 * A run with nobody on it — everyone is excluded, or the team is empty. This is a state to design
 * for, not a failure: the flow must render its empty table and keep the member moving.
 */
export const EMPTY_PAYROLL_RUN_DETAIL: PayrollRunDetail = {
    ...MOCK_PAYROLL_RUN_DETAIL,
    payroll_uuid: '2b7c0e4d-1f33-4f8e-9a21-8f2a1c146d3b',
    items: [],
    totals: ZERO_TOTALS,
};

export const MOCK_BLOCKERS: PayrollBlocker[] = [
    {
        code: 'missing_bank_account',
        message: 'Add a bank account to fund this payroll.',
        severity: 'blocking',
        action_url: '/settings/bank-accounts',
    },
    {
        code: 'employee_missing_tax_details',
        message: "Brandin Podziemski hasn't finished their tax setup.",
        severity: 'blocking',
        action_url: '',
    },
];

/** The provider has not approved the company yet: nothing to fix, only a wait. */
export const COMPANY_IN_REVIEW_BLOCKER: PayrollBlocker = {
    code: 'COMPANY_IN_REVIEW',
    message: "We're reviewing your company details. Payroll can run once the review is done.",
    severity: 'blocking',
    action_url: '',
};

/** The provider says two things stand between this run and submission. Submit is closed from the read. */
const BLOCKED_PAYROLL_RUN_DETAIL: PayrollRunDetail = {
    ...MOCK_PAYROLL_RUN_DETAIL,
    payroll_uuid: '3c8d1f55-2a44-4f8e-9a21-8f2a1c146d3b',
    display_status: 'NEEDS_ATTENTION',
    can_submit: false,
    blockers: MOCK_BLOCKERS,
};

/** The wire's roster rows for the people above, so the standing-rate join has something to join. */
const teamMember = (
    overrides: Partial<TeamMember> & Pick<TeamMember, 'worker_uuid' | 'display_name' | 'first_name' | 'last_name'>
): TeamMember => ({
    business_name: '',
    can_be_paid: true,
    current_salary: null,
    email: `${overrides.first_name.toLowerCase()}@example.com`,
    entity_kind: 'person',
    file_new_hire_report: null,
    flsa_status: '',
    has_ein: null,
    has_ssn: null,
    hourly_rate: null,
    is_owner: false,
    job_title: '',
    member_uuid: null,
    middle_initial: '',
    onboarding_state: 'confirmed',
    pay_schedule: MONTHLY_SCHEDULE,
    pay_ytd: null,
    last_paid: null,
    payment_unit: '',
    preferred_first_name: '',
    start_date: '2024-01-15',
    status: 'active',
    termination_date: null,
    two_percent_shareholder: null,
    w9_status: 'not_applicable',
    wage_type: '',
    work_email: '',
    work_state: '',
    worker_type: 'employee',
    ...overrides,
});

/**
 * Served over `/team/` in the demo and mocked in tests. Jon's $76,000/yr on a monthly schedule is
 * this run's gross times twelve, so his row is *not* flagged; Brandin is hourly and Moses has
 * nothing on file, so both stay unknown rather than being compared against a guess.
 */
export const MOCK_RUN_TEAM_MEMBERS: TeamMember[] = [
    teamMember({
        worker_uuid: salariedWorker.worker_uuid,
        display_name: 'Jon Kuminga',
        first_name: 'Jon',
        last_name: 'Kuminga',
        job_title: 'Engineer',
        current_salary: '76000.00',
        payment_unit: 'year',
    }),
    teamMember({
        worker_uuid: hourlyWorker.worker_uuid,
        display_name: 'Brandin Podziemski',
        first_name: 'Brandin',
        last_name: 'Podziemski',
        job_title: 'Technician',
        hourly_rate: '20.00',
        payment_unit: 'hour',
    }),
    teamMember({
        worker_uuid: partiallyLoadedWorker.worker_uuid,
        display_name: 'Moses Moody',
        first_name: 'Moses',
        last_name: 'Moody',
        job_title: 'Analyst',
    }),
    teamMember({
        worker_uuid: excludedSalariedWorker.worker_uuid,
        display_name: 'Priya Reddy',
        first_name: 'Priya',
        last_name: 'Reddy',
        job_title: 'Designer',
        current_salary: '62400.00',
        payment_unit: 'year',
    }),
    teamMember({
        worker_uuid: excludedHourlyWorker.worker_uuid,
        display_name: 'Tobias Lindgren',
        first_name: 'Tobias',
        last_name: 'Lindgren',
        job_title: 'Technician',
        hourly_rate: '18.00',
        payment_unit: 'hour',
    }),
];

/**
 * The ticket's acceptance criterion as a fixture: the largest real run on record is 28 people, and
 * exactly one of them must be findable by eye. Everyone is salaried on the monthly schedule and
 * paid exactly their standing rate, except:
 *
 * - **index 17** carries a $1,250 bonus, so gross diverges from standing pay and the row is flagged
 * - **indices 5 and 22** are excluded, and are *not* flagged — being left out is not being paid wrong
 *
 * The roster is built beside the run from the same salaries so the join cannot drift from it.
 */
const LARGE_RUN_NAMES = [
    'Ada Lovelace',
    'Grace Hopper',
    'Katherine Johnson',
    'Dorothy Vaughan',
    'Mary Jackson',
    'Annie Easley',
    'Margaret Hamilton',
    'Radia Perlman',
    'Barbara Liskov',
    'Frances Allen',
    'Adele Goldberg',
    'Jean Bartik',
    'Betty Holberton',
    'Kathleen Booth',
    'Evelyn Boyd Granville',
    'Mary Kenneth Keller',
    'Karen Spärck Jones',
    'Shafi Goldwasser',
    'Sophie Wilson',
    'Lynn Conway',
    'Hedy Lamarr',
    'Erna Schneider Hoover',
    'Gladys West',
    'Joan Clarke',
    'Klara Dan von Neumann',
    'Marissa Mayer',
    'Ellen Ochoa',
    'Fei-Fei Li',
] as const;

export const LARGE_RUN_ANOMALY_INDEX = 17;
const LARGE_RUN_EXCLUDED_INDICES = [5, 22] as const;
const LARGE_RUN_BONUS = 1250;

const pad = (index: number) => String(index + 1).padStart(4, '0');

const largeRunPeople = LARGE_RUN_NAMES.map((name, index) => {
    const annualSalary = 60000 + index * 2500;
    const standing = annualSalary / 12;
    const bonus = index === LARGE_RUN_ANOMALY_INDEX ? LARGE_RUN_BONUS : 0;
    const gross = standing + bonus;
    const employeeTaxes = gross * 0.2;
    const employerTaxes = gross * 0.0765;
    const [firstName, ...rest] = name.split(' ');

    const item = worker({
        item_uuid: `b1a2c3d4-${pad(index)}-4c3a-9b7e-1f2a3b4c5d00`,
        worker_uuid: `f1e0a2b3-${pad(index)}-4c3a-9b7e-1f2a3b4c5d00`,
        worker_name: name,
        job_title: 'Engineer',
        excluded: LARGE_RUN_EXCLUDED_INDICES.includes(index as 5 | 22),
        gross_pay: gross.toFixed(2),
        net_pay: (gross - employeeTaxes).toFixed(2),
        lines: [
            earning('other', 'Salary', standing.toFixed(2)),
            ...(bonus > 0 ? [earning(PAYROLL_LINE_CODES.bonus, 'Bonus', bonus.toFixed(2))] : []),
            employeeTax('Federal Income Tax', employeeTaxes.toFixed(2)),
            employerTax('Social Security (Employer)', employerTaxes.toFixed(2)),
        ],
    });

    const member = teamMember({
        worker_uuid: item.worker_uuid,
        display_name: name,
        first_name: firstName,
        last_name: rest.join(' '),
        job_title: 'Engineer',
        current_salary: annualSalary.toFixed(2),
        payment_unit: 'year',
    });

    return { item, member };
});

const sumBy = (items: PayrollRunItem[], pick: (item: PayrollRunItem) => number) =>
    items.reduce((total, item) => total + pick(item), 0).toFixed(2);

const lineTotal = (item: PayrollRunItem, kind: LineKind, party: LineParty) =>
    item.lines.filter((l) => l.kind === kind && l.party === party).reduce((sum, l) => sum + Number(l.amount ?? 0), 0);

const largeRunIncluded = largeRunPeople.map(({ item }) => item).filter((item) => !item.excluded);

const LARGE_PAYROLL_RUN_DETAIL: PayrollRunDetail = {
    ...MOCK_PAYROLL_RUN_DETAIL,
    payroll_uuid: '4d9e2a66-3b55-4f8e-9a21-8f2a1c146d3b',
    items: largeRunPeople.map(({ item }) => item),
    totals: {
        gross_pay: sumBy(largeRunIncluded, (item) => Number(item.gross_pay)),
        net_pay: sumBy(largeRunIncluded, (item) => Number(item.net_pay)),
        employee_taxes: sumBy(largeRunIncluded, (item) => lineTotal(item, 'tax', 'employee')),
        employer_taxes: sumBy(largeRunIncluded, (item) => lineTotal(item, 'tax', 'employer')),
        employee_benefits: '0.00',
        employer_benefits: '0.00',
        reimbursements: '0.00',
        company_debit: sumBy(largeRunIncluded, (item) => Number(item.gross_pay) + lineTotal(item, 'tax', 'employer')),
    },
};

const LARGE_RUN_TEAM_MEMBERS: TeamMember[] = largeRunPeople.map(({ member }) => member);

/**
 * Forced states for stories and the demo. `slow` and `error` are the two the design review keeps
 * asking to see and the two hardest to reach against a happy-path fixture; `blocked` and `large`
 * are the ones this ticket's acceptance criteria are checked against.
 */
export type PayrollRunScenario = 'success' | 'slow' | 'error' | 'empty' | 'blocked' | 'large';

/** Latency for the `slow` scenario — long enough to watch the loading state, short enough to sit through. */
export const SLOW_SCENARIO_LATENCY_MS = 2_000;

const FIXTURE_BY_SCENARIO: Record<Exclude<PayrollRunScenario, 'error'>, PayrollRunDetail> = {
    success: MOCK_PAYROLL_RUN_DETAIL,
    slow: MOCK_PAYROLL_RUN_DETAIL,
    empty: EMPTY_PAYROLL_RUN_DETAIL,
    blocked: BLOCKED_PAYROLL_RUN_DETAIL,
    large: LARGE_PAYROLL_RUN_DETAIL,
};

/** Throws for `error`, so callers surface a failure through the same path a 5xx would. */
export const resolvePayrollRunFixture = (scenario: PayrollRunScenario): PayrollRunDetail => {
    if (scenario === 'error') {
        throw new Error('Payroll run fixture: forced error scenario');
    }

    return FIXTURE_BY_SCENARIO[scenario];
};

/** The roster the demo serves beside each run, so the standing-rate join has the right people. */
export const resolveRunTeamFixture = (scenario: PayrollRunScenario): TeamMember[] =>
    scenario === 'large' ? LARGE_RUN_TEAM_MEMBERS : MOCK_RUN_TEAM_MEMBERS;

/*
 * The same fixtures as the components see them — through the adapter and the roster join, which is
 * the only path the app itself takes. Component tests and stories read these; nothing hand-writes
 * a `PayrollRun`.
 */

const toRun = (detail: PayrollRunDetail, members: TeamMember[] = MOCK_RUN_TEAM_MEMBERS): PayrollRun =>
    applyStandingRates(mapRunDetail(detail), members);

export const MOCK_PAYROLL_RUN: PayrollRun = toRun(MOCK_PAYROLL_RUN_DETAIL);
export const EXCLUDED_EMPLOYEES_PAYROLL_RUN: PayrollRun = toRun(EXCLUDED_EMPLOYEES_PAYROLL_RUN_DETAIL);
export const EMPTY_PAYROLL_RUN: PayrollRun = toRun(EMPTY_PAYROLL_RUN_DETAIL);
export const LARGE_PAYROLL_RUN: PayrollRun = toRun(LARGE_PAYROLL_RUN_DETAIL, LARGE_RUN_TEAM_MEMBERS);
export const BLOCKED_PAYROLL_RUN: PayrollRun = toRun(BLOCKED_PAYROLL_RUN_DETAIL);
